import ChevronDown from '@/icons/chevron-down.svg?react'
import { autoUpdate, computePosition, flip } from '@floating-ui/dom'
import classNames from 'classnames'
import { useSelect } from 'downshift'
import { useEffect, useRef } from 'preact/hooks'
import styles from './index.module.scss'

type Item<T> = {
  value: T
  label: string
  description?: string | null
}

type SelectProps<T> = {
  value?: T
  options: Item<T>[]
  className?: string
  placeholder?: string
  disabled?: boolean
  onChange: (value: T) => void
}

export const Select = <T,>(props: SelectProps<T>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const floatingRef = useRef<HTMLDivElement>(null)

  const select = useSelect({
    items: props.options,
    selectedItem: props.options.find(item => item.value == props.value) ?? null,
    onSelectedItemChange: e => {
      if (e.selectedItem) {
        props.onChange(e.selectedItem.value)
      }
    },
  })

  useEffect(() => {
    if (!select.isOpen || !triggerRef.current || !floatingRef.current) {
      floatingRef.current?.classList.remove(styles.visible)
      return
    }

    const trigger = triggerRef.current
    const floating = floatingRef.current

    return autoUpdate(trigger, floating, () => {
      computePosition(trigger, floating, {
        placement: 'bottom-start',
        middleware: [flip()],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`
        floating.style.top = `${y}px`
        floating.classList.add(styles.visible)
      })
    })
  }, [select.isOpen])

  return (
    <div
      class={classNames(styles.select, props.className, {
        [styles.active]: select.isOpen,
      })}
    >
      <button
        {...select.getToggleButtonProps({
          ref: triggerRef,
        })}
        type='button'
        class={styles.input}
        disabled={props.disabled}
      >
        <div class={styles.ctrl}>
          {select.selectedItem
            ? (
              select.selectedItem.label
            )
            : props.placeholder
            ? (
              <span class={styles.placeholder}>
                {props.placeholder}
              </span>
            )
            : null}
        </div>

        <div class={styles.icon}>
          <ChevronDown />
        </div>
      </button>

      {select.isOpen && (
        <div
          {...select.getMenuProps({
            ref: floatingRef,
          })}
          class={styles.list}
        >
          {props.options.map((item, index) => (
            <li
              key={String(item.value)}
              {...select.getItemProps({ item, index })}
              class={classNames(
                styles.item,
                select.highlightedIndex === index ? styles.hover : null,
                select.selectedItem === item ? styles.selected : null,
              )}
            >
              <div class={styles.label}>
                {item.label}
              </div>

              {item.description && (
                <div class={styles.desc}>
                  {item.description}
                </div>
              )}
            </li>
          ))}
        </div>
      )}
    </div>
  )
}
